// <s-reel-video>: plays a muted looping clip only while it is on screen and motion is allowed.
// The toggle button ([data-reel-toggle]) gives the shopper a persistent pause/play control.
class SReelVideo extends HTMLElement {
  #observer;
  #controller;
  #userPaused = false;

  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-reel-toggle]');
    if (!this.video) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.#userPaused = true;

    this.toggle?.addEventListener(
      'click',
      () => {
        if (this.video.paused) {
          this.#userPaused = false;
          this.video.play().catch(() => {});
        } else {
          this.#userPaused = true;
          this.video.pause();
        }
      },
      { signal }
    );

    this.video.addEventListener('play', () => this.#sync(), { signal });
    this.video.addEventListener('pause', () => this.#sync(), { signal });

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.#userPaused) this.video.play().catch(() => {});
          else this.video.pause();
        }
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);
    this.#sync();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  #sync() {
    if (!this.toggle) return;
    const playing = !this.video.paused;
    this.toggle.setAttribute('aria-pressed', playing ? 'false' : 'true');
    this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel);
    this.toggle.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
    this.toggle.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
  }
}

if (!customElements.get('s-reel-video')) customElements.define('s-reel-video', SReelVideo);
